import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const ModeSelector = ({ onSelect }) => {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Init particles
    particlesRef.current = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: -0.3 - Math.random() * 0.5,
      size: Math.random() * 2.5 + 0.5,
      opacity: Math.random() * 0.6 + 0.1,
      hue: 200 + Math.random() * 60,
    }));

    // Plane trail for selector
    const planes = Array.from({ length: 3 }, (_, i) => ({
      x: -60 + i * 30,
      y: canvas.height * (0.25 + i * 0.2),
      speed: 0.8 + i * 0.3,
      size: 14 - i * 2,
      opacity: 0.12 - i * 0.03,
    }));

    let t = 0;
    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      t += 0.004;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Stars
      particlesRef.current.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -5) { p.y = canvas.height + 5; p.x = Math.random() * canvas.width; }
        if (p.x < -5) p.x = canvas.width + 5;
        if (p.x > canvas.width + 5) p.x = -5;
        ctx.save();
        ctx.globalAlpha = p.opacity * (0.6 + Math.sin(t * 2 + p.x) * 0.4);
        ctx.fillStyle = `hsl(${p.hue}, 80%, 75%)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // Distant planes
      planes.forEach(pl => {
        pl.x += pl.speed;
        if (pl.x > canvas.width + 100) pl.x = -100;
        ctx.save();
        ctx.globalAlpha = pl.opacity;
        ctx.translate(pl.x, pl.y + Math.sin(t * 0.8 + pl.y * 0.01) * 8);
        // draw small plane silhouette
        ctx.fillStyle = 'rgba(102,200,234,1)';
        ctx.beginPath();
        ctx.moveTo(pl.size, 0);
        ctx.lineTo(-pl.size, -pl.size * 0.28);
        ctx.lineTo(-pl.size * 0.5, 0);
        ctx.lineTo(-pl.size, pl.size * 0.28);
        ctx.closePath();
        ctx.fill();
        // contrail
        const grad = ctx.createLinearGradient(-pl.size, 0, -pl.size * 5, 0);
        grad.addColorStop(0, 'rgba(255,255,255,0.25)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(-pl.size * 5, -1, pl.size * 4, 2);
        ctx.restore();
      });
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 });
  };

  return (
    <div className="mode-selector" onMouseMove={handleMouseMove}>
      <canvas ref={canvasRef} className="mode-selector-canvas" />

      {/* Ambient orbs */}
      <div className="mode-selector-orb mode-selector-orb--1"
        style={{ transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)` }} />
      <div className="mode-selector-orb mode-selector-orb--2"
        style={{ transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)` }} />
      <div className="mode-selector-orb mode-selector-orb--3"
        style={{ transform: `translate(${mousePos.x * -15}px, ${mousePos.y * 15}px)` }} />

      <motion.div
        className="mode-selector-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{ transform: `translate(${mousePos.x * 4}px, ${mousePos.y * 4}px)` }}
      >
        {/* Header */}
        <motion.div
          className="mode-selector-header"
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.9, type: 'spring', bounce: 0.4 }}
        >
          <div className="mode-selector-eyebrow">
            <motion.span
              className="mode-selector-dot"
              animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            WALEED ABDULLAH &nbsp;&middot;&nbsp; PORTFOLIO
          </div>
          <h1 className="mode-selector-title">
            Choose Your
            <br />
            <motion.span
              className="mode-selector-title-gradient"
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              style={{ backgroundSize: '300% 100%' }}
            >
              Experience
            </motion.span>
          </h1>
          <p className="mode-selector-subtitle">
            Two ways to explore the same portfolio. Pick the one that suits your mood.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          className="mode-selector-cards"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.9, type: 'spring', bounce: 0.3 }}
        >
          {/* Simple Mode */}
          <motion.button
            className={`mode-card mode-card--simple ${hovered === 'simple' ? 'mode-card--hovered' : ''}`}
            onMouseEnter={() => setHovered('simple')}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect('simple')}
            whileHover={{ y: -8, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="mode-card-glow mode-card-glow--simple" />
            <motion.div
              className="mode-card-icon"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              whileHover={{ scale: 1.12, rotate: -4 }}
            >
              <SimpleIcon />
            </motion.div>
            <div className="mode-card-body">
              <div className="mode-card-badge">CLASSIC</div>
              <h2 className="mode-card-title">Simple Mode</h2>
              <p className="mode-card-desc">
                The portfolio as designed, with clean sections, smooth animations, and
                everything at a scroll.
              </p>
              <ul className="mode-card-features">
                <li>Full portfolio navigation</li>
                <li>Projects &amp; case studies</li>
                <li>Skills &amp; experience</li>
                <li>Contact form</li>
              </ul>
            </div>
            <div className="mode-card-cta">
              <span>Enter Portfolio</span>
              <motion.span
                style={{ display: 'inline-flex' }}
                animate={{ x: hovered === 'simple' ? 5 : 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              >
                <ArrowIcon />
              </motion.span>
            </div>
          </motion.button>

          {/* Divider */}
          <div className="mode-selector-divider">
            <div className="mode-selector-divider-line" />
            <span className="mode-selector-divider-text">OR</span>
            <div className="mode-selector-divider-line" />
          </div>

          {/* Flight Mode */}
          <motion.button
            className={`mode-card mode-card--flight ${hovered === 'flight' ? 'mode-card--hovered' : ''}`}
            onMouseEnter={() => setHovered('flight')}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect('flight')}
            whileHover={{ y: -8, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="mode-card-glow mode-card-glow--flight" />
            <motion.div
              className="mode-card-icon"
              animate={{ y: [0, -6, 0], rotate: [0, 3, 0, -3, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              whileHover={{ scale: 1.12 }}
            >
              <PlaneIcon />
            </motion.div>
            <div className="mode-card-body">
              <div className="mode-card-badge mode-card-badge--flight">IMMERSIVE</div>
              <h2 className="mode-card-title">Flight Mode</h2>
              <p className="mode-card-desc">
                Pilot a plane through an interactive sky. Each checkpoint unlocks a section
                of the portfolio.
              </p>
              <ul className="mode-card-features">
                <li>3D flight environment</li>
                <li>Portfolio checkpoints</li>
                <li>Keyboard &amp; touch controls</li>
                <li>Cinematic experience</li>
              </ul>
            </div>
            <div className="mode-card-cta mode-card-cta--flight">
              <span>Take Flight</span>
              <motion.span
                style={{ display: 'inline-flex' }}
                animate={{ x: hovered === 'flight' ? 5 : 0, y: hovered === 'flight' ? -3 : 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              >
                <PlaneSmallIcon />
              </motion.span>
            </div>
          </motion.button>
        </motion.div>

        {/* Footer hint */}
        <motion.p
          className="mode-selector-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          You can switch between modes at any time
        </motion.p>
      </motion.div>
    </div>
  );
};

const SimpleIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="10" width="32" height="4" rx="2" fill="currentColor" opacity="0.9" />
    <rect x="6" y="20" width="24" height="4" rx="2" fill="currentColor" opacity="0.7" />
    <rect x="6" y="30" width="28" height="4" rx="2" fill="currentColor" opacity="0.5" />
  </svg>
);

const PlaneIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 26L24 8L40 26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.4" />
    <path d="M24 10L36 24H28L32 38H16L20 24H12L24 10Z" fill="currentColor" />
    <circle cx="24" cy="24" r="3" fill="currentColor" opacity="0.6" />
  </svg>
);

const PlaneSmallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 10L10 3L18 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5" />
    <path d="M10 4L16 11H12L14 17H6L8 11H4L10 4Z" fill="currentColor" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default ModeSelector;